import { DATE_FORMATS } from "config/constants"
import { IncidentInterface, PreviousShiftEvent } from "@type/workMonitor.types"
import moment from "moment"
import React, { useMemo } from "react"
import { isInTimeWindow } from "config/utils"
interface Props {
	data: {
		geoFenceBreachCount: number
		incidentEvents: IncidentInterface[]
		checkEvents: PreviousShiftEvent[]
		qrEvents: PreviousShiftEvent[]
		lateCheckThreshold: number
	}
}

const HighlightsSection: React.FC<Props> = ({ data }) => {
	const { geoFenceBreachCount, incidentEvents, checkEvents, qrEvents, lateCheckThreshold } = data

	const lateCheckCallsCount = checkEvents.filter(event =>
		isInTimeWindow(moment(event.dueTime), moment(event.eventTime), { after: lateCheckThreshold }),
	).length

	const highlights = useMemo(() => {
		const highlight: string[] = []
		if (geoFenceBreachCount) {
			highlight.push(`The officer moved out of the assigned area ${geoFenceBreachCount} times.`)
		}
		if (incidentEvents?.length) {
			incidentEvents.forEach(incident => {
				highlight.push(
					`There was an incident with the title ${incident.incident} at 
							${moment(incident.date).format(DATE_FORMATS.hh_mm_DD_MM_YYYY)}.`,
				)
			})
		}
		if (checkEvents.length) {
			const missingCheckEvents = checkEvents.filter(event => event.isEventMissed).length
			if (lateCheckCallsCount === 0 && missingCheckEvents === 0) {
				highlight.push(`All check calls were submitted on time.`)
			} else {
				highlight.push(
					`${lateCheckCallsCount} out of ${checkEvents.length} check calls ${
						lateCheckCallsCount === 1 ? "was" : "were"
					} submitted late. ${missingCheckEvents} ${missingCheckEvents === 1 ? "was" : "were"} missing.`,
				)
			}
		}
		const missedSummary = qrEvents.reduce((accumulator, event) => {
			event.missingQREvents?.forEach(missing => {
				const qrName = missing.qr?.name || "Unknown QR"
				accumulator[qrName] = (accumulator[qrName] || 0) + 1
			})
			return accumulator
		}, {} as Record<string, number>)
		const entries = Object.entries(missedSummary)
		const maxCount = Math.max(0, ...entries.map(([_, count]) => count))
		const maxMissedQR =
			maxCount > 0
				? entries.filter(([_, count]) => count === maxCount).map(([name, count]) => ({ name, count }))
				: []

		if (maxMissedQR.length) {
			const names = maxMissedQR.map(qr => `"${qr.name}"`).join(", ")
			highlight.push(`QR ${names} were missed the most, a total of ${maxMissedQR[0].count} times.`)
		} else {
			highlight.push("No QR patrol was missed.")
		}

		return highlight
	}, [checkEvents, lateCheckCallsCount, qrEvents])

	return (
		<div>
			<div className=" bg-[#B2DDFF]">
				<div className="px-4 py-2 text-3 font-medium">HIGHLIGHTS</div>
			</div>
			<div className="pt-5 pb-4 text-lg font-medium">
				{highlights.length === 0 ? "No highlights to show" : "Following are the brief highlights of the shift:"}
			</div>
			<div className="space-y-4">
				{highlights.map((highlight, index) => (
					<div key={index} className="text-md font-normal">
						{index + 1}- {highlight}
					</div>
				))}
			</div>
		</div>
	)
}

export default HighlightsSection
